import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import Icon from '../../components/Icon';
import { ApiError } from '../../lib/api';
import { adminApi, getAdminAccessToken } from '../lib/adminApi';
import ConfigField, { ConfigRow } from './ConfigField';
import type { AdminThemePalette } from '../../games/types';

import { CATEGORY_META, TIER_META, IMPACT_META, type CatTier, type CategoryInfo } from './widgetSchema';
import { useAdminLang } from './AdminLangContext';
import { tr } from './translations';

// The /api/admin/config/:game endpoint returns the manifest split by
// section (`game` for core/rules/timing/etc. and `bot` for all bot AI
// tunables). Each entry arrives with its current DB `value` inlined,
// so we don't need to cross-reference against a separate db array.
type ManifestEntry = {
  key: string;
  category: string;
  live: boolean;
  desc: string;
  value: unknown;
};
type ApiPayload = {
  game: string;
  manifest: { game?: ManifestEntry[]; bot?: ManifestEntry[] };
  db: { values: Record<string, unknown>; meta?: unknown };
};

type Props = {
  game: string;
  theme: AdminThemePalette;
};

type PendingPayload = {
  game: string;
  lastAppliedAt: string | null;
  pending: Array<{ key: string; updated_at: string; value: unknown }>;
  count: number;
};

type ApplyResult = {
  scope: string;
  log_id: number;
  diff: Record<string, Record<string, unknown>>;
  admin_reload_ok: boolean;
  admin_reload_error: string | null;
  player_reload_ok: boolean;
  player_reload_error: string | null;
  ok: boolean;
};

export default function ConfigPanel({ game, theme }: Props) {
  const t = theme;
  const { lang, setLang } = useAdminLang();
  const [data, setData] = useState<ApiPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // "Everyday only" filter — hides Advanced + Informational tiers so the
  // page shows just the common money/on-off controls.
  const [everydayOnly, setEverydayOnly] = useState(false);
  // Multi-open accordion. Each category is toggled independently so
  // the admin can keep several sections expanded at once (or collapse
  // them all and scroll past the entire panel in a few rows).
  const [openCats, setOpenCats] = useState<Set<string>>(() => new Set());
  const [reloading, setReloading] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  // Pending-changes banner — populated from GET /config/:game/pending
  // after every load. Shows yellow banner with a list of unpublished
  // keys and an "Apply Changes to Live" button. Hidden when count=0.
  const [pending, setPending] = useState<PendingPayload | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  const refreshPending = useCallback(async () => {
    try {
      const p = await adminApi.get<PendingPayload>(`/admin/config/${game}/pending`);
      setPending(p);
    } catch {
      // Pending-fetch failure is non-fatal — the banner just stays
      // empty. Don't surface errors to the admin for this background
      // call; the main config still works.
      setPending(null);
    }
  }, [game]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const d = await adminApi.get<ApiPayload>(`/admin/config/${game}`);
      setData(d);
      await refreshPending();
    } catch (e) {
      setErr((e as ApiError).message || 'Failed to load config');
    } finally {
      setLoading(false);
    }
  }, [game, refreshPending]);

  useEffect(() => { load(); }, [load]);

  const rows: ConfigRow[] = useMemo(() => {
    if (!data) return [];
    const all: ManifestEntry[] = [
      ...(data.manifest.game || []),
      ...(data.manifest.bot || []),
    ];
    return all.map((m) => ({
      meta: { key: m.key, category: m.category, description: m.desc || '', live: m.live },
      value: m.value,
    }));
  }, [data]);

  // TP-7: is the Target Profit engine ON for this game? When it is, it is the
  // single control that drives outcomes/edge, so the panel demotes the knobs it
  // supersedes. When OFF (the default for every game today) nothing changes.
  const targetProfitOn = useMemo(() => {
    const v = rows.find((r) => r.meta.key === 'target_profit_enabled')?.value;
    return v === true || v === 'true' || v === 1 || v === '1';
  }, [rows]);
  const targetProfitPct = useMemo(() => {
    const v = rows.find((r) => r.meta.key === 'target_profit_pct')?.value;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  }, [rows]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      r.meta.key.toLowerCase().includes(q) ||
      r.meta.description.toLowerCase().includes(q) ||
      r.meta.category.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const grouped = useMemo(() => {
    const g: Record<string, ConfigRow[]> = {};
    for (const r of filtered) {
      (g[r.meta.category] ||= []).push(r);
    }
    return g;
  }, [filtered]);

  // Category metadata with a safe fallback so an unmapped category never
  // breaks the UI (defaults to Tier 2 "Advanced").
  const metaFor = (cat: string): CategoryInfo =>
    CATEGORY_META[cat] || { icon: 'folder', label: cat, tier: 2 as CatTier, blurb: '', impact: [] };

  // Order categories by TIER (Everyday → Advanced → Informational), then
  // alphabetically by friendly label within each tier. The "Everyday only"
  // toggle drops Tiers 2 & 3 entirely.
  const categories = useMemo(() => {
    let cats = Object.keys(grouped);
    if (everydayOnly) cats = cats.filter((c) => metaFor(c).tier === 1);
    return cats.sort((a, b) => {
      const ma = metaFor(a), mb = metaFor(b);
      if (ma.tier !== mb.tier) return ma.tier - mb.tier;
      // Within a tier: explicit `order` first (Target Profit is pinned to the
      // very top of Tier 1 as the headline), then alphabetical by label.
      const oa = ma.order ?? 0, ob = mb.order ?? 0;
      if (oa !== ob) return oa - ob;
      return ma.label.localeCompare(mb.label);
    });
  }, [grouped, everydayOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the user is typing a search, auto-expand every matching
  // category so results stay visible. Without this, they'd type a
  // query, see a collapsed row, and have to click to see hits.
  useEffect(() => {
    if (search.trim()) {
      setOpenCats(new Set(categories));
    }
  }, [search, categories.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // On first load of each game, auto-expand the Everyday (Tier-1) sections
  // and leave Advanced / Informational collapsed — so the page opens on the
  // settings that matter, with the niche knobs tucked away below.
  const initFor = useRef<string | null>(null);
  useEffect(() => {
    if (!data || initFor.current === game) return;
    initFor.current = game;
    setOpenCats(new Set(Object.keys(grouped).filter((c) => {
      const m = metaFor(c);
      if (m.tier !== 1) return false;
      // TP-7: when Target Profit is driving, start its superseded controls
      // collapsed so the slider is visually the one in charge.
      if (targetProfitOn && m.supersededByTargetProfit) return false;
      return true;
    })));
  }, [data, game, grouped, targetProfitOn]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleCat(cat: string) {
    setOpenCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  }
  function expandAll()   { setOpenCats(new Set(categories)); }
  function collapseAll() { setOpenCats(new Set()); }

  async function onSave(key: string, value: unknown) {
    // lib/api.ts doesn't export a `put` helper, so call fetch directly
    // while reusing the same access-token + credentials flow.
    //
    // Save = DB write only. The change does NOT reach bg-player /
    // live matches yet — admin must press "Apply Changes to Live"
    // for that. The yellow pending banner appears after this call
    // returns to show the change is queued.
    const res = await fetch(`/api/admin/config/${game}/${encodeURIComponent(key)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'content-type': 'application/json', ...authHeader() },
      body: JSON.stringify({ value }),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new ApiError(j?.error?.code || 'http_error', j?.error?.message || `HTTP ${res.status}`, res.status);
    }
    await load();
    setFlash(`${key} saved — click Apply to push live`);
    setTimeout(() => setFlash(null), 2500);
  }

  async function handleApply() {
    setApplying(true);
    setApplyError(null);
    try {
      const res = await fetch(`/api/admin/config/${game}/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json', ...authHeader() },
      });
      const j: ApplyResult = await res.json().catch(() => ({} as ApplyResult));
      if (!res.ok || !j.ok) {
        // Surface the partial-failure state clearly. The DB writes
        // survive — admin can retry.
        const reasons: string[] = [];
        if (j.admin_reload_ok === false) {
          reasons.push(`bg-admin reload failed: ${j.admin_reload_error || 'unknown'}`);
        }
        if (j.player_reload_ok === false) {
          reasons.push(`bg-player reload failed: ${j.player_reload_error || 'unknown'}`);
        }
        setApplyError(reasons.length ? reasons.join(' · ') : `Apply failed (HTTP ${res.status})`);
      } else {
        setFlash(`Applied ${pending?.count || 0} change(s) to live`);
        setTimeout(() => setFlash(null), 3500);
      }
      await load();
    } finally {
      setApplying(false);
    }
  }

  async function handleReload() {
    setReloading(true);
    try {
      await fetch('/api/admin/config/reload', {
        method: 'POST', credentials: 'include', headers: { ...authHeader() },
      });
      await load();
      setFlash('Configuration hot-reloaded from database');
      setTimeout(() => setFlash(null), 2500);
    } finally {
      setReloading(false);
    }
  }

  const allOpen = openCats.size === categories.length && categories.length > 0;

  return (
    <div>
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Icon name="search" size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={tr('search', lang)}
            className="w-full rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 pl-10 pr-3 py-2.5 text-sm
                       focus:outline-none focus:ring-2 focus:ring-fuchsia-500 focus:border-fuchsia-500"
          />
        </div>

        {/* Language toggle — EN/हिन्दी pill. Switching this flips every
            description, every button label, and which voice the
            read-aloud button uses. Preference is remembered in
            localStorage via AdminLangContext. */}
        <div className="inline-flex rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-0.5" role="group" aria-label="Language toggle">
          <button
            onClick={() => setLang('en')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition
              ${lang === 'en' ? `${t.accent} text-white shadow` : 'text-neutral-400 hover:text-neutral-200'}`}
          >
            EN
          </button>
          <button
            onClick={() => setLang('hi')}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition
              ${lang === 'hi' ? `${t.accent} text-white shadow` : 'text-neutral-400 hover:text-neutral-200'}`}
            lang="hi"
          >
            हिन्दी
          </button>
        </div>

        <button
          onClick={() => setEverydayOnly((v) => !v)}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold transition border
            ${everydayOnly
              ? `${t.accent} text-white border-transparent`
              : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 border-neutral-200 dark:border-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-700'}`}
          title="Show only the common everyday controls (hide Advanced + Informational)."
        >
          <Icon name={everydayOnly ? 'filter_alt' : 'filter_alt_off'} size={16} />
          {everydayOnly ? 'Everyday only' : 'All settings'}
        </button>
        <button
          onClick={allOpen ? collapseAll : expandAll}
          disabled={!categories.length}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold
                     bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-100 dark:bg-neutral-700 text-neutral-800 dark:text-neutral-200
                     disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <Icon name={allOpen ? 'unfold_less' : 'unfold_more'} size={16} />
          {allOpen ? tr('collapseAll', lang) : tr('expandAll', lang)}
        </button>
        <button
          onClick={handleReload}
          disabled={reloading || loading}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-white
                      ${t.accent} ${t.accentHover}
                      disabled:opacity-50 disabled:cursor-not-allowed transition`}
          title="Re-read every config row from the DB into bg-admin's in-memory copy. Does NOT push to bg-player — use Apply for that."
        >
          <Icon name={reloading ? 'progress_activity' : 'refresh'}
                size={16} className={reloading ? 'animate-spin' : ''} />
          {tr('reloadFromDb', lang)}
        </button>
      </div>

      {/* Pending-changes banner.
          Saves write to DB only — they don't reach the live player
          process until an admin presses "Apply Changes to Live"
          here. The yellow banner is loud-and-clear so an admin
          who edited and walked away can't forget there's unpushed
          work. Hidden when count=0. */}
      {pending && pending.count > 0 && (
        <div className="mb-4 rounded-2xl border border-amber-400/50 bg-amber-50 dark:bg-amber-500/10 px-4 py-3 flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl grid place-items-center bg-amber-100 dark:bg-amber-500/20 shrink-0">
            <Icon name="schedule" size={20} className="text-amber-700 dark:text-amber-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-amber-900 dark:text-amber-200 text-[13.5px]">
              {pending.count} unpublished change{pending.count === 1 ? '' : 's'}
            </div>
            <div className="text-[11.5px] text-amber-800/80 dark:text-amber-300/80 mt-0.5 break-words">
              Saved to database — not yet live. Click Apply to push to the player end.
            </div>
            <div className="text-[10.5px] font-mono text-amber-700/70 dark:text-amber-300/60 mt-1 break-all">
              {pending.pending.slice(0, 6).map((p) => p.key).join(', ')}
              {pending.pending.length > 6 ? `, +${pending.pending.length - 6} more` : ''}
            </div>
            {applyError && (
              <div className="mt-2 rounded-lg border border-red-400/50 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-[11.5px] text-red-700 dark:text-red-300 break-words">
                <span className="font-bold">Apply failed: </span>{applyError}
              </div>
            )}
          </div>
          <button
            onClick={handleApply}
            disabled={applying}
            className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
            title="Push every saved change to the live player end now."
          >
            <Icon name={applying ? 'progress_activity' : 'rocket_launch'} size={16}
                  className={applying ? 'animate-spin' : ''} />
            {applying ? 'Applying…' : 'Apply Changes to Live'}
          </button>
        </div>
      )}

      <AnimatePresence>
        {flash && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="mb-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-200 px-4 py-2 text-sm inline-flex items-center gap-2"
          >
            <Icon name="check_circle" size={16} filled /> {flash}
          </motion.div>
        )}
      </AnimatePresence>

      {/* TP-7 supersession banner. Only shows when the Target Profit engine
          is switched ON for this game — it then owns the game's outcome/edge,
          so the controls it replaces are marked "Superseded" and collapsed.
          Off (every game today) → this never renders and the panel is unchanged. */}
      {!loading && !err && targetProfitOn && (
        <div className="mb-4 rounded-2xl border border-indigo-400/50 bg-indigo-50 dark:bg-indigo-500/10 px-4 py-3 flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl grid place-items-center bg-indigo-100 dark:bg-indigo-500/20 shrink-0">
            <Icon name="track_changes" size={20} className="text-indigo-700 dark:text-indigo-300" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-indigo-900 dark:text-indigo-200 text-[13.5px]">
              Target Profit is ON{targetProfitPct != null ? ` — aiming to keep ${targetProfitPct}% of every bet` : ''}
            </div>
            <div className="text-[11.5px] text-indigo-800/80 dark:text-indigo-300/80 mt-0.5">
              The engine now drives this game&apos;s outcomes automatically. The edge &amp; bot-strength controls it replaces
              are marked <span className="font-semibold">Superseded</span> and start collapsed below — they take effect again
              only if you switch Target Profit off.
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="p-10 text-center text-neutral-400">
          <Icon name="progress_activity" size={28} className="animate-spin inline-block" /> Loading…
        </div>
      )}
      {err && (
        <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-200 text-sm">
          {err}
        </div>
      )}

      {!loading && !err && categories.map((cat, i) => {
        const meta = metaFor(cat);
        const isOpen = openCats.has(cat);
        const items = grouped[cat];
        // TP-7: this category's job is currently taken over by the Target
        // Profit engine — mute it and badge it so the slider reads as the
        // one control in charge. Only when Target Profit is actually ON.
        const superseded = targetProfitOn && !!meta.supersededByTargetProfit;
        // Tier divider before the first category of each tier (suppressed
        // while "Everyday only" has collapsed the list to a single tier).
        const prevTier = i > 0 ? metaFor(categories[i - 1]).tier : 0;
        const showDivider = !everydayOnly && meta.tier !== prevTier;
        const tmeta = TIER_META[meta.tier];
        return (
          <div key={cat}>
          {showDivider && (
            <div className="flex items-center gap-2 mt-6 mb-2 px-1">
              <Icon name={tmeta.icon} size={16} className="text-neutral-400 shrink-0" />
              <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 shrink-0">{tmeta.label}</span>
              <span className="text-[11px] text-neutral-400 hidden md:inline truncate">— {tmeta.blurb}</span>
              <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-800" />
            </div>
          )}
          <div className="mb-3 rounded-2xl bg-white dark:bg-neutral-900/60 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
            <button
              onClick={() => toggleCat(cat)}
              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-neutral-100 dark:bg-neutral-800/40 transition ${superseded ? 'opacity-60' : ''}`}
            >
              <div className={`w-10 h-10 rounded-xl grid place-items-center ${t.softBg} shrink-0`}>
                <Icon name={meta.icon} size={20} className="text-neutral-900 dark:text-neutral-100" />
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">{meta.label}</h3>
                  {superseded && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border text-indigo-700 dark:text-indigo-300 border-indigo-400/40 bg-indigo-500/10">
                      <Icon name="track_changes" size={11} /> Superseded by Target Profit
                    </span>
                  )}
                  {meta.tier === 3 && (
                    <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border text-neutral-500 border-neutral-400/40 bg-neutral-500/10">Rarely changed</span>
                  )}
                  {meta.impact.map((im) => (
                    <span key={im} className={`inline-flex items-center gap-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded border ${IMPACT_META[im].cls}`}>
                      <Icon name={IMPACT_META[im].icon} size={11} /> {IMPACT_META[im].label}
                    </span>
                  ))}
                </div>
                {meta.blurb && <p className="text-[11px] text-neutral-400 mt-0.5 truncate">{meta.blurb}</p>}
                <p className="text-[10px] text-neutral-400/70 mt-0.5">{items.length} setting{items.length === 1 ? '' : 's'}</p>
              </div>
              <Icon name={isOpen ? 'expand_less' : 'expand_more'} size={22} className="text-neutral-400 shrink-0" />
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="p-4 space-y-3 border-t border-neutral-200 dark:border-neutral-800">
                    {items.map((r) => (
                      <ConfigField key={r.meta.key} row={r} theme={theme} game={game} onSave={onSave} />
                    ))}
                    {/* A second "close this section" button pinned at the
                        bottom so an admin at the end of a long expanded
                        panel can collapse it without scrolling back up. */}
                    <div className="flex justify-center pt-1">
                      <button
                        onClick={() => toggleCat(cat)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                                   bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 transition"
                      >
                        <Icon name="unfold_less" size={14} />
                        {tr('closeSection', lang)}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          </div>
        );
      })}

      {!loading && !err && !categories.length && (
        <div className="p-8 text-center text-neutral-400">{tr('noMatches', lang)}</div>
      )}
    </div>
  );
}

function authHeader(): Record<string, string> {
  const t = getAdminAccessToken();
  return t ? { authorization: `Bearer ${t}` } : {};
}
